import { Box, Grid } from '@mui/material';
import Text from 'components/text';
import { useEffect, useState } from 'react';
import api from 'utils/api';
import Snackbar from '@mui/material/Snackbar';
import BaseInput from 'components/inputs/base-input';
import { useUserData } from 'hooks/useUserData';
import InvitedRow, { InvitationInterface } from './invited-row';

const BoxWrapper = ({
  value,
  title,
  active = false,
  onClick,
}: {
  value: number;
  title: string;
  active?: boolean;
  onClick?: () => void;
}) => {
  return (
    <Box
      textAlign={'center'}
      border={1}
      borderRadius={1}
      borderColor={active ? '#ccc' : '#eee'}
      paddingX={1}
      paddingY={0.5}
      onClick={onClick}
      flexGrow={1}
      sx={{
        cursor: 'pointer',
        ':hover': {
          backgroundColor: '#f0f0f0',
        },
      }}
      bgcolor={active ? '#e2e1e5' : 'transparent'}
    >
      <Text fontSize={20}>{value}</Text>
      <Text fontSize={16} paddingY={0.25}>
        {title}
      </Text>
    </Box>
  );
};

export default function InvitedList() {
  const { userData, permissionsData } = useUserData();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(-1);
  const [toast, setToast] = useState<string | null>(null);

  const [invites, setInvites] = useState<InvitationInterface[]>([]);
  const [allInvites, setAllInvites] = useState<InvitationInterface[]>([]);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [searchName, setSearchName] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedInviter, setSelectedInviter] = useState('all');

  const [totalInvites, setTotalInvites] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [totalAccepted, setTotalAccepted] = useState(0);
  const [totalInvitedByMe, setTotalInvitedByMe] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.invitation.getInvitedArtists().then((res) => {
      setAllInvites(res as InvitationInterface[]);
      setTotalInvites(res.length);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const filteredAllInvites = allInvites
      .filter((invite) => {
        if (selectedInviter !== 'all') {
          return invite.inviterid === Number(selectedInviter);
        }

        return true;
      })
      .filter((invite) => {
        if (selectedStatus === 'all') {
          return (invite.artistname || '').toLowerCase().includes(searchName.toLowerCase());
        } else {
          return (
            invite.artistname.toLowerCase().includes(searchName.toLowerCase()) &&
            invite.status === selectedStatus
          );
        }
      });

    setInvites(filteredAllInvites.slice((page - 1) * rowsPerPage, page * rowsPerPage));

    if ((selectedInviter === 'all' || searchName !== '') && selectedStatus === 'all') {
      setTotalInvites(filteredAllInvites.length);
      setTotalPending(filteredAllInvites.filter((invite) => invite.status === 'pending').length);
      setTotalAccepted(filteredAllInvites.filter((invite) => invite.status === 'accepted').length);
    }

    setTotalInvitedByMe(
      filteredAllInvites.filter((invite) => invite.inviterid === userData.id).length
    );
    setTotalPages(Math.ceil(filteredAllInvites.length / rowsPerPage));
  }, [allInvites, page, rowsPerPage, searchName, selectedStatus, selectedInviter, userData.id]);

  return (
    <Box height="calc(100vh - 125px)" overflow="auto">
      <Box padding={2}>
        <Box marginBottom={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Box display="flex" gap={2}>
                <BaseInput
                  SelectProps={{ displayEmpty: true }}
                  variant="filled"
                  type="text"
                  label="Search Invited Artists"
                  error={false}
                  sx={{
                    width: '100%',
                  }}
                  onChange={(e) => {
                    setSearchName(e.target.value);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box display="flex" gap={2}>
                <Box
                  display="flex"
                  border={1}
                  borderColor="#ccc"
                  padding={0.5}
                  borderRadius={1}
                  gap={1}
                  flexGrow={1}
                >
                  <BoxWrapper
                    title="All Invites"
                    value={totalInvites}
                    active={selectedStatus === 'all'}
                    onClick={() => {
                      setPage(1);
                      setSelectedStatus('all');
                    }}
                  />
                  <BoxWrapper
                    title="Total Pending"
                    value={totalPending}
                    active={selectedStatus === 'pending'}
                    onClick={() => {
                      setPage(1);
                      setSelectedStatus('pending');
                    }}
                  />
                  <BoxWrapper
                    title="Total Accepted"
                    value={totalAccepted}
                    active={selectedStatus === 'accepted'}
                    onClick={() => {
                      setPage(1);
                      setSelectedStatus('accepted');
                    }}
                  />
                </Box>
                <Box
                  display="flex"
                  border={1}
                  borderColor="#ccc"
                  padding={0.5}
                  borderRadius={1}
                  gap={1}
                  flexGrow={1}
                >
                  <BoxWrapper
                    title="My Invites"
                    value={totalInvitedByMe}
                    active={selectedInviter !== 'all'}
                    onClick={() => {
                      setPage(1);
                      if (selectedInviter === 'all') {
                        setSelectedInviter(userData?.id?.toString() || 'all');
                      } else {
                        setSelectedInviter('all');
                      }
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {loading && <Text fontSize={18}>Loading invites...</Text>}

      {!loading && allInvites.length && !invites.length && (
        <Box marginX={2} padding={2} border={1} borderColor="#ddd" borderRadius={1}>
          <Text fontSize={18}>No artist found</Text>
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap={2} paddingX={2}>
        {invites.map((invite) => (
          <InvitedRow
            key={invite.id}
            invite={invite}
            open={open === invite.id}
            onToggle={() => setOpen(open === invite.id ? -1 : invite.id)}
            canEdit={!!permissionsData?.admin}
            onSaved={(id, patch) =>
              setAllInvites((prev) =>
                prev.map((inv) => (inv.id === id ? { ...inv, ...patch } : inv))
              )
            }
            onToast={setToast}
          />
        ))}
      </Box>
      <Box paddingBottom={4}>
        {totalPages > 1 && <Pagination totalPages={totalPages} page={page} setPage={setPage} />}
      </Box>
      <Snackbar
        open={!!toast}
        autoHideDuration={1500}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}

const Pagination = ({
  totalPages,
  page,
  setPage,
}: {
  totalPages: number;
  page: number;
  setPage: (page: number) => void;
}) => {
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const paginationRange = getPaginationRange();

  return (
    <Box display="flex" justifyContent="center" marginTop={2}>
      {paginationRange.map((pageNumber, index) => {
        if (typeof pageNumber === 'string') {
          return (
            <Box key={index} padding={2} margin={'0 0.5rem'}>
              <Text fontSize={16}>{pageNumber}</Text>
            </Box>
          );
        }

        return (
          <Box
            key={index}
            onClick={() => setPage(pageNumber)}
            border={1}
            borderColor={pageNumber === page ? '#ccc' : 'transparent'}
            bgcolor={pageNumber === page ? '#f0f0f0' : 'transparent'}
            padding={2}
            margin={'0 0.5rem'}
            borderRadius={2}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Text fontSize={16}>{pageNumber}</Text>
          </Box>
        );
      })}
    </Box>
  );
};
